<template>
  <div class="gender-ei-profile-comparison">
    <a-card
        :bordered="true"
        class="gender-ei-profile-comparison__card"
    >
      <div class="gender-ei-profile-comparison__header">
        <h3 class="gender-ei-profile-comparison__title">
          {{ averageTitle }}
        </h3>
      </div>

      <div class="gender-ei-profile-comparison__legend">
        <div class="gender-ei-profile-comparison__legend-item">
          <span
              class="gender-ei-profile-comparison__legend-mark"
              :style="{ backgroundColor: firstColor }"
          ></span>

          <span>{{ firstLabel }}</span>
        </div>

        <div class="gender-ei-profile-comparison__legend-item">
          <span
              class="gender-ei-profile-comparison__legend-mark"
              :style="{ backgroundColor: secondColor }"
          ></span>

          <span>{{ secondLabel }}</span>
        </div>
      </div>

      <EiScaleRowsTable
          class="gender-ei-profile-comparison__scale-table"
          :sections="normalizedAverageProfileSections"
          mode="compare"
          :show-track-values="true"
          :show-difference="false"
          :show-scale-after-each-section="true"
          :show-first-section-title="false"
          :default-color="secondColor"
          :default-compare-color="firstColor"
          :scale-height="12"
          :compare-scale-height="12"
          :row-gap="12"
      />
    </a-card>

    <a-card
        :bordered="true"
        class="gender-ei-profile-comparison__card"
    >
      <div class="gender-ei-profile-comparison__header">
        <h3 class="gender-ei-profile-comparison__title">
          {{ detailsTitle }}
        </h3>
      </div>

      <div class="gender-ei-profile-comparison__legend">
        <div class="gender-ei-profile-comparison__legend-item">
          <span
              class="gender-ei-profile-comparison__legend-mark"
              :style="{ backgroundColor: firstColor }"
          ></span>

          <span>{{ firstLabel }}</span>
        </div>

        <div class="gender-ei-profile-comparison__legend-item">
          <span
              class="gender-ei-profile-comparison__legend-mark"
              :style="{ backgroundColor: secondColor }"
          ></span>

          <span>{{ secondLabel }}</span>
        </div>
      </div>

      <EiScaleRowsTable
          class="gender-ei-profile-comparison__scale-table"
          :sections="normalizedDetailsSections"
          mode="compare"
          :show-track-values="true"
          :show-difference="false"
          :show-scale-after-each-section="false"
          :show-first-section-title="true"
          :default-color="secondColor"
          :default-compare-color="firstColor"
          :scale-height="12"
          :compare-scale-height="12"
          :row-gap="12"
      />
    </a-card>
  </div>
</template>

<script>
import { computed } from 'vue'
import EiScaleRowsTable from "@/components/Analytics/EiScaleRowsTable.vue";


export default {
  name: 'GenderEiProfileComparisonBlock',

  components: {
    EiScaleRowsTable
  },

  props: {
    averageTitle: {
      type: String,
      default: 'Средний ЭИ-профиль',
    },

    detailsTitle: {
      type: String,
      default: 'Детализация ЭИ (10 разделов способностей)',
    },

    firstLabel: {
      type: String,
      default: 'Мужчины',
    },

    secondLabel: {
      type: String,
      default: 'Женщины',
    },

    firstColor: {
      type: String,
      default: '#7FA2F4',
    },

    secondColor: {
      type: String,
      default: '#F28FB1',
    },

    averageProfileSections: {
      type: Array,
      default: () => [],
    },

    detailsSections: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const normalizedAverageProfileSections = computed(() => {
      return normalizeSections(props.averageProfileSections, props.firstColor, props.secondColor)
    })

    const normalizedDetailsSections = computed(() => {
      return normalizeSections(props.detailsSections, props.firstColor, props.secondColor)
    })

    return {
      normalizedAverageProfileSections,
      normalizedDetailsSections,
    }
  },
}

function normalizeSections(sections, firstColor, secondColor) {
  return (Array.isArray(sections) ? sections : []).map((section, sectionIndex) => ({
    ...section,
    key: section.key || `section-${sectionIndex}`,
    rows: Array.isArray(section.rows)
        ? section.rows.map((row, rowIndex) => ({
          ...row,
          key: row.key || `row-${rowIndex}`,
          label: row.label || '',
          value: toNumber(row.value),
          compareValue: toNumber(row.compareValue),
          color: row.color || secondColor,
          compareColor: row.compareColor || firstColor,
        }))
        : [],
  }))
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.gender-ei-profile-comparison {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  margin-top: 20px;
}

.gender-ei-profile-comparison__card {
  min-width: 0;
  border-radius: 8px;
}

.gender-ei-profile-comparison__header {
  margin-bottom: 12px;
}

.gender-ei-profile-comparison__title {
  margin: 0;
  min-width: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.gender-ei-profile-comparison__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.gender-ei-profile-comparison__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.gender-ei-profile-comparison__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.gender-ei-profile-comparison__scale-table {
  width: 100%;
}

.gender-ei-profile-comparison__scale-table :deep(.ei-scale-table__bottom-scale) {
  margin-bottom: 0;
}

@media (max-width: $tablet-breakpoint) {
  .gender-ei-profile-comparison {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .gender-ei-profile-comparison__card :deep(.ant-card-body) {
    padding: 14px 12px;
  }

  .gender-ei-profile-comparison__title {
    font-size: 15px;
  }

  .gender-ei-profile-comparison__scale-table :deep(.ei-scale-table__label) {
    font-size: 12px;
  }
}
</style>